import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Users, Calendar, Package, Clock, FileText, 
  Settings, Bell, HelpCircle, ChevronDown, ChevronUp,
  Play, Shield, CreditCard
} from 'lucide-react';

const helpGuides = [
  {
    id: 'employees',
    icon: Users,
    title: 'Managing Employees',
    description: 'Add, edit, and manage your team members',
    content: [
      {
        title: 'Adding a New Employee',
        steps: [
          'Go to the Employees tab in your dashboard',
          'Click "Add Employee" button in the top right',
          'Fill in the employee details: name, email, phone, position, and hourly rate',
          'Select the employee type (Field-based or Office-based)',
          'Choose the appropriate role for their access level',
          'Click "Add Employee" to create the account',
          'The employee will receive an email with login credentials'
        ]
      },
      {
        title: 'Editing Employee Details',
        steps: [
          'Find the employee in the Employees tab',
          'Click the "Manage..." dropdown next to their name',
          'Select "Edit Details"',
          'Update the necessary information',
          'Click "Save Changes"'
        ]
      },
      {
        title: 'Managing Employee Hours',
        steps: [
          'Company Admins can reset all hours for an employee',
          'Accounts role can adjust hours (add or subtract)',
          'All adjustments are tracked with reasons for audit purposes',
          'Use "Manage..." > "Adjust Hours" to make changes'
        ]
      }
    ]
  },
  {
    id: 'schedule',
    icon: Calendar,
    title: 'Scheduling & Assignments',
    description: 'Create and manage work schedules',
    content: [
      {
        title: 'Creating a Schedule',
        steps: [
          'Navigate to the Schedule tab',
          'Click "Add Schedule" button',
          'Select the employee(s) to schedule',
          'Choose the project/location',
          'Set the date, start time, and end time',
          'Add any notes if needed',
          'Click "Create Schedule"'
        ]
      },
      {
        title: 'Sending Daily Assignments',
        steps: [
          'Click "Send Daily Assignments" from the dashboard header',
          'This sends push notifications to all scheduled employees',
          'Employees receive their assignment details for the day',
          'Works best when employees have enabled notifications'
        ]
      },
      {
        title: 'Viewing Schedules',
        steps: [
          'Use List View for a simple list of upcoming schedules',
          'Use Week View to see the full week at a glance',
          'Use Calendar View for monthly planning',
          'On mobile, List View is the default for easy scrolling'
        ]
      }
    ]
  },
  {
    id: 'projects',
    icon: Package,
    title: 'Project Management',
    description: 'Create and track projects',
    content: [
      {
        title: 'Creating a Project',
        steps: [
          'Go to the Projects tab',
          'Click "Add Project" button',
          'Enter project name and address',
          'Set the start date and expected end date',
          'Add team members to the project',
          'Optionally assign equipment from the pool',
          'Click "Create Project"'
        ]
      },
      {
        title: 'Assigning Team Members',
        steps: [
          'Open the project details',
          'Click "Manage Team"',
          'Select employees to add to the project',
          'Set their work schedule if needed',
          'Employees will see this project in their dashboard'
        ]
      }
    ]
  },
  {
    id: 'time',
    icon: Clock,
    title: 'Time Tracking',
    description: 'Clock in/out and track hours',
    content: [
      {
        title: 'Clocking In/Out (For Employees)',
        steps: [
          'Click the "Clock In" button on your dashboard',
          'Allow location access when prompted',
          'Your clock-in time and location are recorded',
          'When finished, click "Clock Out"',
          'Your hours are automatically calculated'
        ]
      },
      {
        title: 'Viewing Time Reports (For Admins)',
        steps: [
          'Click on "Clocked In Now" card to see who\'s working',
          'Click on "This Week\'s Hours" for detailed breakdown',
          'Export time reports from the Excel Exports tab',
          'Filter by date range, employee, or project'
        ]
      }
    ]
  },
  {
    id: 'equipment',
    icon: Package,
    title: 'Equipment Management',
    description: 'Track and assign equipment',
    content: [
      {
        title: 'Adding Equipment to Pool',
        steps: [
          'Go to the Equipment tab',
          'Click "Add Equipment"',
          'Enter equipment name and description',
          'Set the total quantity available',
          'Choose the unit type (units, pieces, etc.)',
          'Click "Add Equipment"'
        ]
      },
      {
        title: 'Assigning Equipment to Projects',
        steps: [
          'Find the equipment in the list',
          'Click "Assign" button',
          'Select the project',
          'Enter quantity to assign',
          'Available quantity will update automatically'
        ]
      }
    ]
  },
  {
    id: 'notifications',
    icon: Bell,
    title: 'Notifications',
    description: 'Set up push notifications',
    content: [
      {
        title: 'Enabling Push Notifications',
        steps: [
          'Click the notification bell icon in the header',
          'Click "Enable Notifications"',
          'Allow notifications when browser prompts',
          'You\'ll now receive shift reminders and updates'
        ]
      },
      {
        title: 'Shift Reminders',
        steps: [
          'Go to Settings > Notification Settings tab',
          'Configure reminder times (e.g., 30 mins before shift)',
          'Set the reminder message template',
          'Employees receive automatic reminders before their shifts'
        ]
      }
    ]
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Company Settings',
    description: 'Configure your company profile',
    content: [
      {
        title: 'Updating Company Profile',
        steps: [
          'Click "Settings" button on the dashboard',
          'Upload your company logo',
          'Set your company name and tagline',
          'Configure business hours',
          'Set the bi-weekly pay period start date',
          'Click "Save Changes"'
        ]
      }
    ]
  }
];

const faqItems = [
  {
    question: 'How do I reset a forgotten password?',
    answer: 'If an employee forgets their password, a Company Admin can reset it from the Employees tab. Click Manage > and the password will be shown or can be reset. The employee should change it after logging in.'
  },
  {
    question: 'Can employees clock in from anywhere?',
    answer: 'Yes, employees can clock in from any location. However, their GPS location is recorded with each clock-in, so managers can verify they are at the correct job site.'
  },
  {
    question: 'How do I export time reports for payroll?',
    answer: 'Go to the Excel Exports tab and select the date range. You can export by employee, project, or all time entries. The export includes hours worked, hourly rates, and total costs.'
  },
  {
    question: 'What happens after my trial ends?',
    answer: 'After the 5-day trial, you\'ll need to subscribe to continue using the service. Your data is preserved, and you can upgrade from the dashboard. Contact support if you need an extension.'
  },
  {
    question: 'Can I have multiple company admins?',
    answer: 'Each company can have one primary Company Admin. However, you can assign the General Manager role to other users who need similar administrative access.'
  },
  {
    question: 'How do shift reminders work?',
    answer: 'Shift reminders are sent via push notifications at your configured time before a shift starts. Make sure employees have enabled notifications on their devices for this to work.'
  },
  {
    question: 'Can I track expenses for projects?',
    answer: 'Yes! Use the Expenses tab to log project-related expenses. You can attach receipts and categorize expenses by type. These are included in project cost reports.'
  },
  {
    question: 'How do I assign equipment to multiple projects?',
    answer: 'Equipment is managed as a pool. You can assign portions of your equipment to different projects. For example, if you have 10 ladders, you can assign 3 to Project A and 5 to Project B, leaving 2 available.'
  }
];

export function HelpCenter({ onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const filteredGuides = helpGuides.filter(guide =>
    guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    guide.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFaqs = faqItems.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="help-center p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-blue-600" />
            Help Center
          </h1>
          <p className="text-gray-600">Find guides, tutorials, and answers to common questions</p>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>Close</Button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Tabs defaultValue="guides" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="guides">
            <FileText className="h-4 w-4 mr-2" />
            Guides
          </TabsTrigger>
          <TabsTrigger value="faq">
            <HelpCircle className="h-4 w-4 mr-2" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="quick-start">
            <Play className="h-4 w-4 mr-2" />
            Quick Start
          </TabsTrigger>
        </TabsList>

        {/* Guides Tab */}
        <TabsContent value="guides">
          <div className="grid gap-4">
            {filteredGuides.map((guide) => {
              const Icon = guide.icon;
              const isExpanded = expandedGuide === guide.id;
              
              return (
                <Card 
                  key={guide.id} 
                  className={`cursor-pointer transition-shadow hover:shadow-md ${isExpanded ? 'ring-2 ring-blue-200' : ''}`}
                >
                  <CardHeader 
                    className="pb-2"
                    onClick={() => setExpandedGuide(isExpanded ? null : guide.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{guide.title}</CardTitle>
                          <CardDescription>{guide.description}</CardDescription>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent>
                      <div className="space-y-6 pt-2">
                        {guide.content.map((section, idx) => (
                          <div key={idx}>
                            <h4 className="font-semibold text-gray-800 mb-3">{section.title}</h4>
                            <ol className="list-decimal list-inside space-y-2">
                              {section.steps.map((step, stepIdx) => (
                                <li key={stepIdx} className="text-gray-600 pl-2">
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq">
          <div className="space-y-3">
            {filteredFaqs.map((faq, idx) => {
              const isExpanded = expandedFaq === idx;
              
              return (
                <Card 
                  key={idx}
                  className="cursor-pointer"
                  onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                >
                  <CardHeader className="py-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium">{faq.question}</CardTitle>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  </CardHeader>
                  {isExpanded && (
                    <CardContent className="pt-0">
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Quick Start Tab */}
        <TabsContent value="quick-start">
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Welcome to WorkForce Pro!</h3>
                <p className="opacity-90 mb-4">
                  Get your team up and running in just a few minutes with these quick setup steps.
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
                    <CardTitle className="text-lg">Set Up Your Company</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Click Settings to add your logo</li>
                    <li>• Enter your company name</li>
                    <li>• Set your bi-weekly pay period</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</div>
                    <CardTitle className="text-lg">Add Your Team</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Go to Employees tab</li>
                    <li>• Click "Add Employee"</li>
                    <li>• Enter their details & role</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">3</div>
                    <CardTitle className="text-lg">Create Projects</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Go to Projects tab</li>
                    <li>• Click "Add Project"</li>
                    <li>• Assign team members</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">4</div>
                    <CardTitle className="text-lg">Schedule Shifts</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Go to Schedule tab</li>
                    <li>• Create shifts for employees</li>
                    <li>• Send daily assignments</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Shield className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-800 mb-1">You're All Set!</h4>
                    <p className="text-green-700">
                      Once you've completed these steps, your team can start clocking in, 
                      tracking time, and managing projects. Employees will receive their 
                      login credentials via email.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default HelpCenter;
